import {useEffect, useMemo, useState, type ReactNode} from 'react';
import {latestClosedBusinessDay} from '@ozon-ai/periods';
import type {CompetitorComparison} from '@ozon-ai/sellmonitor/contracts';
import {
	COMPETITOR_ANALYSIS_SIGNAL_LABELS,
	COMPETITOR_DISCOVERY_RUN_STATUS_LABELS,
	COMPETITOR_EVIDENCE_SOURCE_LABELS,
} from '@ozon-ai/sellmonitor/contracts';
import {CartesianGrid, Line, LineChart, XAxis, YAxis} from 'recharts';
import {Alert, AlertDescription} from '@/components/ui/alert.tsx';
import {Badge} from '@/components/ui/badge.tsx';
import {Button} from '@/components/ui/button.tsx';
import {Card, CardContent, CardHeader, CardTitle} from '@/components/ui/card.tsx';
import {
	ChartContainer,
	ChartLegend,
	ChartLegendContent,
	ChartTooltip,
	ChartTooltipContent,
	type ChartConfig,
} from '@/components/ui/chart.tsx';
import {Input} from '@/components/ui/input.tsx';
import {Checkbox} from '@/components/ui/checkbox.tsx';
import {Label} from '@/components/ui/label.tsx';
import {RadioGroup, RadioGroupItem} from '@/components/ui/radio-group.tsx';
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select.tsx';
import {
	Sheet,
	SheetContent,
	SheetDescription,
	SheetHeader,
	SheetTitle,
} from '@/components/ui/sheet.tsx';
import {Spinner} from '@/components/ui/spinner.tsx';
import {Switch} from '@/components/ui/switch.tsx';
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@/components/ui/table.tsx';
import {ToggleGroup, ToggleGroupItem} from '@/components/ui/toggle-group.tsx';
import {flowAmountClassName} from '../unit-economics-page/format-money';
import {useProductPageContext} from './product-context';
import {useCompetitorAnalysis, type AnalysisPeriodChoice} from './use-competitor-analysis';

type SortKey = 'price' | 'sales' | 'growth' | 'position';
type RowFilter = 'all' | 'direct_competitor' | 'close_analogue';

const PERIOD_OPTIONS: {value: AnalysisPeriodChoice; label: string}[] = [
	{value: '7', label: '7 дней'},
	{value: '30', label: '30 дней'},
	{value: '90', label: '90 дней'},
	{value: 'custom', label: 'Произвольный период'},
];

const CHART_COLORS = ['var(--chart-1)', 'var(--chart-2)', 'var(--chart-3)', 'var(--chart-4)', 'var(--chart-5)'];

function formatDay(value: string | undefined): string {
	if (!value) return '—';
	const [year, month, day] = value.split('-');
	if (!year || !month || !day) return value;
	return `${day}.${month}.${year}`;
}

function formatMoney(value: number | undefined): string {
	if (value == null) return '—';
	return `${value.toLocaleString('ru-RU', {maximumFractionDigits: 2})} ₽`;
}

function formatCount(value: number | undefined): string {
	if (value == null) return '—';
	return value.toLocaleString('ru-RU', {maximumFractionDigits: 1});
}

function formatSigned(value: number | undefined): string {
	if (value == null) return '—';
	const sign = value > 0 ? '+' : '';
	return `${sign}${value.toLocaleString('ru-RU', {maximumFractionDigits: 1})}%`;
}

function ChangePercent({value}: {value: number | undefined}) {
	return <span className={`tabular-nums ${flowAmountClassName(value ?? null)}`}>{formatSigned(value)}</span>;
}

function OurSalesValue({sales, change}: {sales: number | undefined; change: number | undefined}) {
	if (sales == null && change == null) return <span className="text-muted-foreground">—</span>;
	if (sales == null) return <ChangePercent value={change}/>;
	if (change == null) return <>{formatCount(sales)}</>;
	return <>{formatCount(sales)} · <ChangePercent value={change}/></>;
}

function summaryWithoutStaleSalesGap(text: string, sales: number | undefined): string {
	if (sales == null) return text;
	return text
		.replace('За выбранный период нет готового ProductAnalysis, поэтому наши продажи в сравнение не включены. ', '')
		.replace('Начисления Ozon за период не получены, поэтому наши продажи в сравнение не включены. ', '');
}

export function CompetitorAnalysisPanel({
	offerId,
	view,
	sharedPeriod,
	focusIds = [],
	onFocusIdsChange,
}: {
	offerId: string;
	view: 'summary' | 'comparison';
	sharedPeriod?: {from: string; to: string} | null;
	focusIds?: string[];
	onFocusIdsChange?: (ids: string[]) => void;
}) {
	const page = useProductPageContext();
	const state = useCompetitorAnalysis(offerId);
	const [preset, setPreset] = useState<AnalysisPeriodChoice>('30');
	const [from, setFrom] = useState('');
	const [to, setTo] = useState('');
	const [includeAnalogues, setIncludeAnalogues] = useState(false);
	const [sortKey, setSortKey] = useState<SortKey>('sales');
	const [rowFilter, setRowFilter] = useState<RowFilter>('all');
	const [selectedId, setSelectedId] = useState<string | null>(null);
	const [chartGrain, setChartGrain] = useState<'week' | 'month'>('week');
	const maxDay = latestClosedBusinessDay();
	const result = state.result;
	const sameWindow = Boolean(
		sharedPeriod &&
		result &&
		sharedPeriod.from === result.context.period.from &&
		sharedPeriod.to === result.context.period.to,
	);
	const ourSales = sameWindow && page.salesDynamics ? page.salesDynamics.summary.sold : result?.ownProduct.sales;
	const ourPreviousSales = sameWindow && page.previousSalesDynamics
		? page.previousSalesDynamics.summary.sold
		: result?.ownProduct.previousSales;
	const ourSalesChange = ourSales != null && ourPreviousSales != null && ourPreviousSales !== 0
		? ((ourSales - ourPreviousSales) / Math.abs(ourPreviousSales)) * 100
		: result?.marketSummary.ourSalesChangePercent;
	const ourRevenue = sameWindow && page.unitEconomics ? page.unitEconomics.sales.total : result?.ownProduct.revenue;
	const selected = result?.competitors.find((item) => item.dwhProductId === selectedId) ?? null;

	const rows = useMemo(() => {
		const items = (result?.competitors ?? []).filter((item) => rowFilter === 'all' || item.relationType === rowFilter);
		const valueOf = (item: CompetitorComparison) => {
			if (sortKey === 'price') return item.currentPrice ?? Number.POSITIVE_INFINITY;
			if (sortKey === 'sales') return -(item.sales ?? -1);
			if (sortKey === 'growth') return -(item.salesChangePercent ?? -9999);
			return item.medianPosition ?? Number.POSITIVE_INFINITY;
		};
		return [...items].sort((left, right) => valueOf(left) - valueOf(right));
	}, [result, rowFilter, sortKey]);

	function refresh() {
		if (sharedPeriod) {
			void state.start({period: sharedPeriod, includeCloseAnalogues: includeAnalogues});
			return;
		}
		if (preset === 'custom' && (!from || !to)) return;
		void state.start({
			period: preset === 'custom' ? {from, to} : {preset},
			includeCloseAnalogues: includeAnalogues,
		});
	}

	function toggleFocus(id: string, checked: boolean) {
		if (!onFocusIdsChange) return;
		if (checked) {
			if (focusIds.includes(id) || focusIds.length >= 5) return;
			onFocusIdsChange([...focusIds, id]);
			return;
		}
		onFocusIdsChange(focusIds.filter((item) => item !== id));
	}

	useEffect(() => {
		if (view !== 'comparison' || !onFocusIdsChange || focusIds.length > 0 || !result) return;
		const top = [...result.competitors]
			.sort((left, right) => (right.sales ?? -1) - (left.sales ?? -1))
			.slice(0, 4)
			.map((item) => item.dwhProductId);
		if (top.length > 0) onFocusIdsChange(top);
	}, [focusIds.length, onFocusIdsChange, result, view]);

	const visibleRows = view === 'summary'
		? rows.slice(0, 4)
		: focusIds.length > 0
			? rows.filter((item) => focusIds.includes(item.dwhProductId))
			: rows;

	return (
		<section className="flex min-w-0 flex-col gap-4">
			<Card size="sm" className="min-w-0">
				<CardHeader>
					<CardTitle>Анализ конкурентов</CardTitle>
				</CardHeader>
				<CardContent className="flex flex-col gap-3">
					<p className="text-sm text-muted-foreground">
						Период: {result ? `${formatDay(result.context.period.from)}–${formatDay(result.context.period.to)}` : 'ещё не выбран'}
					</p>
					<p className="text-sm text-muted-foreground">
						Прямых конкурентов: {result?.marketSummary.directCompetitorCount ?? '—'}
						{state.run?.finishedAt ? `. Обновлено: ${new Date(state.run.finishedAt).toLocaleString('ru-RU', {timeZone: 'Europe/Moscow', dateStyle: 'medium', timeStyle: 'short'})}` : ''}
					</p>
					<div className="flex flex-wrap items-center gap-3">
						{sharedPeriod ? null : (
							<Select
								value={preset}
								items={PERIOD_OPTIONS}
								onValueChange={(value) => value && setPreset(value as AnalysisPeriodChoice)}
							>
								<SelectTrigger className="w-56">
									<SelectValue placeholder="Период"/>
								</SelectTrigger>
								<SelectContent>
									{PERIOD_OPTIONS.map((option) => (
										<SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
									))}
								</SelectContent>
							</Select>
						)}
						<Label>
							<Switch checked={includeAnalogues} onCheckedChange={setIncludeAnalogues}/>
							Включить близкие аналоги
						</Label>
						<Button type="button" disabled={state.running} onClick={refresh}>
							{state.running ? <Spinner data-icon="inline-start"/> : null}
							Обновить анализ
						</Button>
					</div>
					{sharedPeriod == null && preset === 'custom' ? (
						<div className="flex flex-wrap gap-3">
							<Input type="date" max={maxDay} value={from} onChange={(event) => setFrom(event.target.value)} aria-label="Начало периода"/>
							<Input type="date" max={maxDay} value={to} onChange={(event) => setTo(event.target.value)} aria-label="Конец периода"/>
						</div>
					) : null}
					{state.run ? (
						<p className="text-sm text-muted-foreground">
							Статус: {COMPETITOR_DISCOVERY_RUN_STATUS_LABELS[state.run.status]}
							{result ? `. Качество данных: ${qualityLabel(result.dataQuality.status)}` : ''}
							{result?.dataQuality.priceAsOf ? `. Цены на ${new Date(result.dataQuality.priceAsOf).toLocaleString('ru-RU', {timeZone: 'Europe/Moscow', dateStyle: 'medium', timeStyle: 'short'})}` : ''}
						</p>
					) : null}
				</CardContent>
			</Card>

			{state.error ? (
				<Alert variant="destructive">
					<AlertDescription>{state.error}</AlertDescription>
				</Alert>
			) : null}
			{state.loading ? <p className="text-sm text-muted-foreground">Загружаем анализ…</p> : null}
			{!state.loading && !result && !state.running ? (
				<p className="text-sm text-muted-foreground">
					Анализ ещё не запускался. Он использует подтверждённых прямых конкурентов и не предлагает новую цену.
				</p>
			) : null}

			{sharedPeriod && result && (result.context.period.from !== sharedPeriod.from || result.context.period.to !== sharedPeriod.to) ? (
				<p className="text-sm text-muted-foreground">
					Показан анализ за {formatDay(result.context.period.from)}–{formatDay(result.context.period.to)}. Выбранный период другой: обновите анализ, чтобы пересчитать его.
				</p>
			) : null}

			{result && view === 'summary' ? (
				<>
					<div className="grid grid-cols-2 gap-4">
						<SummaryCard title="Цена" lines={[
							['Наша цена', formatMoney(result.marketSummary.ourPrice)],
							['Медиана рынка', formatMoney(result.marketSummary.medianCompetitorPrice)],
							['Отклонение', <ChangePercent value={result.marketSummary.priceDifferenceToMedian}/>],
						]}/>
						<SummaryCard title="Продажи" lines={[
							['Наши продажи', <OurSalesValue sales={ourSales} change={ourSalesChange}/>],
							['Медиана конкурентов', <ChangePercent value={result.marketSummary.medianCompetitorSalesChangePercent}/>],
						]}/>
						<SummaryCard title="Видимость" lines={[
							['Наша медианная позиция', formatCount(result.marketSummary.ourMedianPosition)],
							['Медиана конкурентов', formatCount(result.marketSummary.competitorMedianPosition)],
						]}/>
						<SummaryCard title="Состояние рынка" lines={[
							['Растут', `${result.marketSummary.growingCount} из ${result.marketSummary.directCompetitorCount}`],
							['Снижаются', `${result.marketSummary.decliningCount} из ${result.marketSummary.directCompetitorCount}`],
							['Нет данных', `${result.marketSummary.missingSalesCount} из ${result.marketSummary.directCompetitorCount}`],
						]}/>
					</div>
					<p className="text-sm">{summaryWithoutStaleSalesGap(result.summaryText, ourSales)}</p>
					{result.signals.length > 0 ? (
						<div className="flex flex-wrap gap-2">
							{result.signals.map((signal) => (
								<Badge key={signal.code} variant="outline">{COMPETITOR_ANALYSIS_SIGNAL_LABELS[signal.code]}</Badge>
							))}
						</div>
					) : null}
					{visibleRows.length > 0 ? (
						<ul className="flex flex-col gap-2">
							{visibleRows.map((item) => (
								<li key={item.dwhProductId} className="flex items-baseline justify-between gap-3 text-sm">
									<span className="min-w-0 truncate">{item.title || item.dwhProductId}</span>
									<span className="shrink-0 tabular-nums">{formatMoney(item.currentPrice)} · <ChangePercent value={item.salesChangePercent}/></span>
								</li>
							))}
						</ul>
					) : (
						<p className="text-sm text-muted-foreground">Подтверждённых конкурентов в этом анализе нет.</p>
					)}
				</>
			) : null}

			{result && view === 'comparison' ? (
				<>
					<div className="flex flex-col gap-2">
						<p className="text-sm text-muted-foreground">На графике одновременно не больше пяти конкурентов. Наша карточка остаётся на графике всегда.</p>
						<div className="flex flex-col gap-2">
							{rows.map((item) => (
								<Label key={item.dwhProductId}>
									<Checkbox
										checked={focusIds.includes(item.dwhProductId)}
										disabled={!focusIds.includes(item.dwhProductId) && focusIds.length >= 5}
										onCheckedChange={(checked) => toggleFocus(item.dwhProductId, checked === true)}
									/>
									{item.title || item.dwhProductId}
								</Label>
							))}
						</div>
					</div>
					<RadioGroup
						value={rowFilter}
						onValueChange={(value) => setRowFilter(value as RowFilter)}
						aria-label="Какие строки показать"
						className="flex flex-row flex-wrap gap-x-4 gap-y-2"
					>
						<Label><RadioGroupItem value="all"/>Все</Label>
						<Label><RadioGroupItem value="direct_competitor"/>Прямые конкуренты</Label>
						<Label><RadioGroupItem value="close_analogue"/>Близкие аналоги</Label>
					</RadioGroup>

					<Table className="min-w-[720px]">
						<TableHeader>
							<TableRow>
								<TableHead>Товар</TableHead>
								<SortHead label="Цена" active={sortKey === 'price'} onClick={() => setSortKey('price')}/>
								<TableHead>К медиане</TableHead>
								<SortHead label="Продажи" active={sortKey === 'sales'} onClick={() => setSortKey('sales')}/>
								<SortHead label="Динамика" active={sortKey === 'growth'} onClick={() => setSortKey('growth')}/>
								<TableHead>Выручка</TableHead>
								<SortHead label="Позиция" active={sortKey === 'position'} onClick={() => setSortKey('position')}/>
								<TableHead>Рейтинг</TableHead>
								<TableHead>Остаток</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							<TableRow>
								<TableCell className="max-w-xs whitespace-normal">
									<strong>Наш товар</strong>
									<Badge variant="outline">Ozon</Badge>
								</TableCell>
								<TableCell>{formatMoney(result.ownProduct.currentPrice)}</TableCell>
								<TableCell><ChangePercent value={result.marketSummary.priceDifferenceToMedian}/></TableCell>
								<TableCell>{formatCount(ourSales)}</TableCell>
								<TableCell><ChangePercent value={ourSalesChange}/></TableCell>
								<TableCell>{formatMoney(ourRevenue)}</TableCell>
								<TableCell>{formatCount(result.ownProduct.medianPosition)}</TableCell>
								<TableCell>{formatCount(result.ownProduct.rating)}</TableCell>
								<TableCell>{formatCount(result.ownProduct.stock)}</TableCell>
							</TableRow>
							{visibleRows.map((item) => (
								<TableRow key={item.dwhProductId} className="cursor-pointer" onClick={() => setSelectedId(item.dwhProductId)}>
									<TableCell className="max-w-xs whitespace-normal">
										{item.title || item.dwhProductId}
										<Badge variant="outline">Sellmonitor</Badge>
										{item.relationType === 'close_analogue' ? <Badge variant="secondary">Аналог</Badge> : null}
										{!item.priceComparable ? <Badge variant="outline">Комплектность</Badge> : null}
									</TableCell>
									<TableCell>{formatMoney(item.currentPrice)}</TableCell>
									<TableCell>—</TableCell>
									<TableCell>{formatCount(item.sales)}</TableCell>
									<TableCell><ChangePercent value={item.salesChangePercent}/></TableCell>
									<TableCell>{formatMoney(item.revenue)}</TableCell>
									<TableCell>{formatCount(item.medianPosition)}</TableCell>
									<TableCell>{formatCount(item.rating)}</TableCell>
									<TableCell>{formatCount(item.stock)}</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>

					<ComparisonSheet
						open={selected != null}
						own={result.ownProduct}
						competitor={selected}
						onOpenChange={(open) => {
							if (!open) setSelectedId(null);
						}}
					/>

					<div className="flex flex-col gap-3">
						<div className="flex flex-wrap items-center gap-3">
							<h2 className="text-base font-medium">Динамика продаж</h2>
							<ToggleGroup value={[chartGrain]} onValueChange={(value) => {
								const next = value[value.length - 1];
								if (next === 'week' || next === 'month') setChartGrain(next);
							}}>
								<ToggleGroupItem value="week">Недели</ToggleGroupItem>
								<ToggleGroupItem value="month">Месяцы</ToggleGroupItem>
							</ToggleGroup>
						</div>
						<SalesChart series={focusedSeries(chartGrain === 'week' ? result.salesSeries.week : result.salesSeries.month, focusIds)}/>
						<h2 className="text-base font-medium">
							Позиции в выдаче{result.visibility.query ? `: ${result.visibility.query}` : ''}
						</h2>
						<PositionChart series={focusedSeries(result.positionSeries, focusIds)}/>
					</div>
				</>
			) : null}
		</section>
	);
}

function focusedSeries<T extends {dwhProductId: string; isOwn: boolean}>(series: T[], focusIds: string[]): T[] {
	if (focusIds.length === 0) return series.filter((item) => item.isOwn).slice(0, 1);
	const focus = new Set(focusIds);
	return series.filter((item) => item.isOwn || focus.has(item.dwhProductId));
}

function SummaryCard({title, lines}: {title: string; lines: Array<[string, ReactNode]>}) {
	return (
		<Card size="sm">
			<CardHeader>
				<CardTitle>{title}</CardTitle>
			</CardHeader>
			<CardContent className="flex flex-col gap-1">
				{lines.map(([label, value]) => (
					<p key={label} className="flex justify-between gap-3 text-sm">
						<span className="text-muted-foreground">{label}</span>
						<span>{value}</span>
					</p>
				))}
			</CardContent>
		</Card>
	);
}

function SortHead({label, active, onClick}: {label: string; active: boolean; onClick: () => void}) {
	return (
		<TableHead>
			<Button type="button" variant={active ? 'secondary' : 'ghost'} size="sm" onClick={onClick}>
				{label}
			</Button>
		</TableHead>
	);
}

function qualityLabel(status: 'ok' | 'degraded' | 'unavailable'): string {
	if (status === 'ok') return 'достаточно';
	if (status === 'degraded') return 'частично';
	return 'недостаточно';
}

function SalesChart({series}: {series: {dwhProductId: string; title: string; points: Array<{from: string; units?: number}>}[]}) {
	if (series.length === 0 || series.every((item) => item.points.length === 0)) {
		return <p className="text-sm text-muted-foreground">Для этого шага нет точек продаж.</p>;
	}
	const config = Object.fromEntries(series.map((item, index) => [
		item.dwhProductId,
		{label: item.title, color: CHART_COLORS[index % CHART_COLORS.length]},
	])) satisfies ChartConfig;
	const labels = series[0]?.points.map((point) => point.from) ?? [];
	const data = labels.map((from) => {
		const row: Record<string, string | number | null> = {label: formatDay(from).slice(0, 5)};
		for (const item of series) {
			row[item.dwhProductId] = item.points.find((point) => point.from === from)?.units ?? null;
		}
		return row;
	});
	return (
		<ChartContainer config={config} className="aspect-auto h-72 w-full">
			<LineChart data={data} margin={{left: 8, right: 8, top: 8}}>
				<CartesianGrid vertical={false}/>
				<XAxis dataKey="label" tickLine={false} axisLine={false} tickMargin={8}/>
				<YAxis allowDecimals={false} tickLine={false} axisLine={false} width={36}/>
				<ChartTooltip content={<ChartTooltipContent/>}/>
				<ChartLegend content={<ChartLegendContent/>}/>
				{series.map((item) => (
					<Line
						key={item.dwhProductId}
						type="monotone"
						dataKey={item.dwhProductId}
						stroke={`var(--color-${item.dwhProductId})`}
						strokeWidth={2}
						dot={false}
						connectNulls={false}
					/>
				))}
			</LineChart>
		</ChartContainer>
	);
}

function PositionChart({series}: {series: {dwhProductId: string; title: string; points: Array<{observedAt: string; position?: number}>}[]}) {
	if (series.length === 0) {
		return <p className="text-sm text-muted-foreground">История позиций за доступное окно не получена.</p>;
	}
	const config = Object.fromEntries(series.map((item, index) => [
		item.dwhProductId,
		{label: item.title, color: CHART_COLORS[index % CHART_COLORS.length]},
	])) satisfies ChartConfig;
	const dates = [...new Set(series.flatMap((item) => item.points.map((point) => point.observedAt.slice(0, 10))))].sort();
	const data = dates.map((date) => {
		const row: Record<string, string | number | null> = {label: formatDay(date).slice(0, 5)};
		for (const item of series) {
			const point = item.points.find((entry) => entry.observedAt.slice(0, 10) === date);
			row[item.dwhProductId] = point?.position ?? null;
		}
		return row;
	});
	return (
		<ChartContainer config={config} className="aspect-auto h-72 w-full">
			<LineChart data={data} margin={{left: 8, right: 8, top: 8}}>
				<CartesianGrid vertical={false}/>
				<XAxis dataKey="label" tickLine={false} axisLine={false} tickMargin={8}/>
				<YAxis reversed allowDecimals={false} tickLine={false} axisLine={false} width={36}/>
				<ChartTooltip content={<ChartTooltipContent/>}/>
				<ChartLegend content={<ChartLegendContent/>}/>
				{series.map((item) => (
					<Line
						key={item.dwhProductId}
						type="monotone"
						dataKey={item.dwhProductId}
						stroke={`var(--color-${item.dwhProductId})`}
						strokeWidth={2}
						dot={false}
						connectNulls={false}
					/>
				))}
			</LineChart>
		</ChartContainer>
	);
}

function ComparisonSheet({
	open,
	own,
	competitor,
	onOpenChange,
}: {
	open: boolean;
	own: {
		title?: string;
		currentPrice?: number;
		salesChangePercent?: number;
		medianPosition?: number;
		rating?: number;
		reviewCount?: number;
	};
	competitor: CompetitorComparison | null;
	onOpenChange: (open: boolean) => void;
}) {
	return (
		<Sheet open={open} onOpenChange={onOpenChange}>
			<SheetContent className="overflow-y-auto sm:max-w-xl">
				<SheetHeader>
					<SheetTitle>{competitor?.title || 'Сравнение'}</SheetTitle>
					<SheetDescription>Факты по двум карточкам. Причина изменения продаж здесь не устанавливается.</SheetDescription>
				</SheetHeader>
				{competitor ? (
					<div className="flex flex-col gap-4 px-4 pb-4">
						<Table>
							<TableHeader>
								<TableRow>
									<TableHead>Наш товар</TableHead>
									<TableHead/>
									<TableHead>{competitor.title || 'Конкурент'}</TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								<Pair label="Цена" own={formatMoney(own.currentPrice)} other={formatMoney(competitor.currentPrice)}/>
								<Pair label="Продажи" own={<ChangePercent value={own.salesChangePercent}/>} other={<ChangePercent value={competitor.salesChangePercent}/>}/>
								<Pair label="Позиция" own={formatCount(own.medianPosition)} other={formatCount(competitor.medianPosition)}/>
								<Pair label="Рейтинг" own={formatCount(own.rating)} other={formatCount(competitor.rating)}/>
								<Pair label="Отзывы" own={formatCount(own.reviewCount)} other={formatCount(competitor.reviewCount)}/>
							</TableBody>
						</Table>
						<div>
							<h3 className="text-sm font-medium">Совпадает</h3>
							<p className="text-sm text-muted-foreground">{competitor.matchedCharacteristics.join(', ') || 'нет общих характеристик'}</p>
						</div>
						<div>
							<h3 className="text-sm font-medium">Отличается</h3>
							<p className="text-sm text-muted-foreground">{competitor.differentCharacteristics.join(', ') || 'явных различий в общих характеристиках нет'}</p>
						</div>
						<div>
							<h3 className="text-sm font-medium">Общие запросы</h3>
							<Table>
								<TableHeader>
									<TableRow>
										<TableHead>Запрос</TableHead>
										<TableHead>Наша позиция</TableHead>
										<TableHead>Конкурент</TableHead>
									</TableRow>
								</TableHeader>
								<TableBody>
									{competitor.sharedQueries.length === 0 ? (
										<TableRow>
											<TableCell colSpan={3}>Общих запросов нет</TableCell>
										</TableRow>
									) : competitor.sharedQueries.map((query) => (
										<TableRow key={query.query}>
											<TableCell>{query.query}</TableCell>
											<TableCell>{formatCount(query.ownPosition)}</TableCell>
											<TableCell>{formatCount(query.competitorPosition)}</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</div>
						<div>
							<h3 className="text-sm font-medium">Почему считается конкурентом</h3>
							{competitor.evidence.length === 0 ? (
								<p className="text-sm text-muted-foreground">Свидетельства поиска не сохранены</p>
							) : (
								<ul className="flex flex-col gap-1 text-sm text-muted-foreground">
									{competitor.evidence.map((item, index) => (
										<li key={`${item.source}-${item.query}-${index}`}>
											{COMPETITOR_EVIDENCE_SOURCE_LABELS[item.source]}
											{item.query ? `: ${item.query}` : ''}
											{item.position != null ? `, позиция ${item.position}` : ''}
										</li>
									))}
								</ul>
							)}
						</div>
					</div>
				) : null}
			</SheetContent>
		</Sheet>
	);
}

function Pair({label, own, other}: {label: string; own: ReactNode; other: ReactNode}) {
	return (
		<TableRow>
			<TableCell>{own}</TableCell>
			<TableCell>{label}</TableCell>
			<TableCell>{other}</TableCell>
		</TableRow>
	);
}
